import { useMemo, useState, useEffect } from "react";
import {
  Building2, Layers, FileText, MapPin, Users, Plus, Check, ArrowLeft,
  Search, Trash2, ChevronRight, RotateCcw,
} from "lucide-react";
import api from "../../lib/api";
import { AddressAutocomplete } from "../AddressAutocomplete";
import { GstVerifyField } from "../GstVerifyField";

const STEPS = [
  { label: "Company", icon: Building2 },
  { label: "Division", icon: Layers },
  { label: "GST", icon: FileText },
  { label: "Address", icon: MapPin },
  { label: "POC", icon: Users },
];

const emptyAddr = { label: "", address: "", kind: "both" };
const emptyPoc = { name: "", phone: "", email: "", designation: "" };

// Guided, de-duplicating directory builder: Company → Division → GST → Address → POC.
// At every level you search & pick an existing record first, and only create a new
// one when it truly doesn't exist — which is what stops duplicate companies,
// addresses and POCs from piling up.
// NOTE: every button is type="button" and there are no nested <form>s, so using this
// inside another form (e.g. the LR form) never accidentally submits/closes it.
export function DirectoryWizard({ onChange }) {
  const [companies, setCompanies] = useState([]);
  const [step, setStep] = useState(1);
  const [company, setCompany] = useState(null);
  const [division, setDivision] = useState(null);
  const [bundle, setBundle] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");

  const [cQuery, setCQuery] = useState("");
  const [dQuery, setDQuery] = useState("");
  const [gst, setGst] = useState("");
  const [addrForm, setAddrForm] = useState(emptyAddr);
  const [pocForm, setPocForm] = useState(emptyPoc);

  async function loadCompanies() {
    const { data } = await api.get("/customers");
    setCompanies(data || []);
  }
  useEffect(() => { loadCompanies(); }, []);

  async function reloadBundle(cid) {
    const id = cid || company?.id;
    if (!id) return null;
    const { data } = await api.get(`/directory/company/${id}`);
    setBundle(data);
    window.dispatchEvent(new Event("directory-changed"));
    return data;
  }

  const divisions = bundle?.departments || [];
  const divAddresses = (bundle?.addresses || []).filter((a) => a.department_id === division?.id);
  const divPocs = (bundle?.employees || []).filter((e) => e.department_id === division?.id);

  // ---------- Step 1: Company ----------
  const companyMatches = useMemo(() => {
    const q = cQuery.trim().toLowerCase();
    return (companies || []).filter((c) => !q || c.name.toLowerCase().includes(q)).slice(0, 8);
  }, [cQuery, companies]);
  const exactCompany = (companies || []).find(
    (c) => c.name.trim().toLowerCase() === cQuery.trim().toLowerCase());

  async function pickCompany(c) {
    setCompany(c); setDivision(null); setMsg("");
    await reloadBundle(c.id);
    setStep(2);
  }
  async function createCompany() {
    const name = cQuery.trim();
    if (!name) return;
    setBusy(true); setMsg("");
    try {
      const { data } = await api.post("/customers", { name });
      await loadCompanies();
      onChange?.();
      window.dispatchEvent(new Event("directory-changed"));
      await pickCompany(data);
      setCQuery("");
    } catch (e) {
      setMsg(e?.response?.data?.detail || "Could not create company");
    } finally { setBusy(false); }
  }

  // ---------- Step 2: Division ----------
  const divisionMatches = divisions.filter(
    (d) => !dQuery.trim() || d.name.toLowerCase().includes(dQuery.trim().toLowerCase()));
  const exactDivision = divisions.find(
    (d) => d.name.trim().toLowerCase() === dQuery.trim().toLowerCase());

  function pickDivision(d) {
    setDivision(d); setGst(d.gst_number || ""); setMsg(""); setStep(3);
  }
  async function createDivision() {
    const name = dQuery.trim();
    if (!name) return;
    setBusy(true); setMsg("");
    try {
      const { data } = await api.post("/departments", { company_id: company.id, name });
      await reloadBundle();
      pickDivision(data);
      setDQuery("");
    } catch (e) {
      setMsg(e?.response?.data?.detail || "Could not create division");
    } finally { setBusy(false); }
  }

  // ---------- Step 3: GST ----------
  const gstDupe = gst.trim() && divisions.some(
    (d) => d.id !== division?.id && (d.gst_number || "").trim().toUpperCase() === gst.trim().toUpperCase());

  async function saveGst() {
    setBusy(true); setMsg("");
    try {
      const { data } = await api.put(`/departments/${division.id}`, {
        company_id: company.id, name: division.name, gst_number: gst.trim(), notes: division.notes || "",
      });
      setDivision(data);
      await reloadBundle();
      setStep(4);
    } catch (e) {
      setMsg(e?.response?.data?.detail || "Could not save GST");
    } finally { setBusy(false); }
  }

  // ---------- Step 4: Addresses ----------
  async function addAddress() {
    if (!addrForm.address.trim()) return;
    setBusy(true); setMsg("");
    try {
      await api.post("/company-addresses", {
        company_id: company.id, department_id: division.id,
        label: addrForm.label.trim(), address: addrForm.address.trim(),
        kind: addrForm.kind, gst: gst.trim(),
      });
      setAddrForm(emptyAddr);
      await reloadBundle();
    } catch (e) {
      setMsg(e?.response?.data?.detail || "Could not add address");
    } finally { setBusy(false); }
  }
  async function delAddress(id) {
    if (!confirm("Remove this address?")) return;
    await api.delete(`/company-addresses/${id}`); await reloadBundle();
  }

  // ---------- Step 5: POCs ----------
  const pocDupe = pocForm.name.trim() && divPocs.some(
    (p) => p.name.trim().toLowerCase() === pocForm.name.trim().toLowerCase());

  async function addPoc() {
    if (!pocForm.name.trim()) return;
    setBusy(true); setMsg("");
    try {
      await api.post("/contacts", {
        company_id: company.id, department_id: division.id,
        name: pocForm.name.trim(), phone: pocForm.phone.trim(),
        email: pocForm.email.trim(), designation: pocForm.designation.trim(),
      });
      setPocForm(emptyPoc);
      await reloadBundle();
    } catch (e) {
      setMsg(e?.response?.data?.detail || "Could not add POC");
    } finally { setBusy(false); }
  }
  async function delPoc(id) {
    if (!confirm("Remove this POC?")) return;
    await api.delete(`/contacts/${id}`); await reloadBundle();
  }

  function resetAll() {
    setStep(1); setCompany(null); setDivision(null); setBundle(null);
    setCQuery(""); setDQuery(""); setGst(""); setAddrForm(emptyAddr); setPocForm(emptyPoc); setMsg("");
  }
  function addAnotherDivision() {
    setDivision(null); setGst(""); setDQuery(""); setAddrForm(emptyAddr); setPocForm(emptyPoc); setMsg(""); setStep(2);
  }

  return (
    <section className="lg:col-span-2 card-surface p-5" data-testid="directory-wizard">
      <div className="flex items-center gap-2 mb-1">
        <Building2 className="w-4 h-4 text-[#C84B31]" />
        <div className="section-title mb-0">Company Directory</div>
      </div>
      <p className="text-[12px] text-[#68665E] mb-4">
        Build it top-down so nothing gets duplicated — pick what already exists, only add what's new.
      </p>

      {/* Progress */}
      <div className="flex items-center gap-1.5 mb-5 overflow-x-auto" data-testid="wizard-steps">
        {STEPS.map((s, i) => {
          const n = i + 1;
          const active = step === n;
          const done = step > n;
          const Icon = s.icon;
          return (
            <div key={s.label} className="flex items-center gap-1.5 shrink-0">
              <div
                className={`flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold transition-colors ${
                  active ? "bg-[#C84B31] text-white"
                  : done ? "bg-[#E7F0E7] text-[#2F6B34]"
                  : "bg-[#F1EFEA] text-[#9A968C]"}`}
                data-testid={`wizard-step-${s.label.toLowerCase()}`}
              >
                {done ? <Check className="w-3.5 h-3.5" /> : <Icon className="w-3.5 h-3.5" />}
                {s.label}
              </div>
              {i < STEPS.length - 1 && <ChevronRight className="w-3.5 h-3.5 text-[#C9C5BC]" />}
            </div>
          );
        })}
      </div>

      {/* Breadcrumb of current selection */}
      {(company || division) && (
        <div className="flex flex-wrap items-center gap-2 text-xs mb-4" data-testid="wizard-breadcrumb">
          {company && <span className="rounded bg-[#F2F6FB] border border-[#DCE7F6] px-2 py-1 font-medium text-[#1A1A18]">{company.name}</span>}
          {division && <><ChevronRight className="w-3 h-3 text-[#C9C5BC]" /><span className="rounded bg-[#F2F6FB] border border-[#DCE7F6] px-2 py-1 font-medium text-[#1A1A18]">{division.name}</span></>}
        </div>
      )}

      {msg && <div className="badge-error mb-3 py-2 px-3" data-testid="wizard-error">{msg}</div>}

      {/* ---------- Step 1 ---------- */}
      {step === 1 && (
        <div data-testid="wizard-company-step">
          <label className="block text-xs font-semibold text-[#68665E] mb-1.5">Search or add a company</label>
          <div className="relative">
            <Search className="w-4 h-4 text-[#9A968C] absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              className="input-field w-full pl-9" placeholder="Start typing a company name…"
              value={cQuery} onChange={(e) => setCQuery(e.target.value)} data-testid="wizard-company-search" autoFocus
            />
          </div>
          <div className="mt-2 space-y-1">
            {companyMatches.map((c) => (
              <button type="button" key={c.id} onClick={() => pickCompany(c)} data-testid={`wizard-company-pick-${c.id}`}
                className="flex items-center justify-between w-full text-left rounded border border-[#EAE7E0] hover:border-[#C84B31] hover:bg-[#FBF3F1] px-3 py-2 transition-colors">
                <span className="font-medium text-[#1A1A18] text-sm">{c.name}</span>
                <span className="text-[11px] text-[#9A968C]">Select <ChevronRight className="w-3 h-3 inline" /></span>
              </button>
            ))}
            {cQuery.trim() && !exactCompany && (
              <button type="button" onClick={createCompany} disabled={busy} data-testid="wizard-company-create"
                className="flex items-center gap-2 w-full text-left rounded border border-dashed border-[#C84B31] text-[#C84B31] hover:bg-[#FBF3F1] px-3 py-2 text-sm font-semibold transition-colors disabled:opacity-50">
                <Plus className="w-4 h-4" /> Create new company “{cQuery.trim()}”
              </button>
            )}
            {!cQuery.trim() && companyMatches.length === 0 && (
              <p className="text-xs text-[#9A968C] py-2">No companies yet — type a name to add your first one.</p>
            )}
          </div>
        </div>
      )}

      {/* ---------- Step 2 ---------- */}
      {step === 2 && company && (
        <div data-testid="wizard-division-step">
          <div className="flex items-center justify-between mb-1.5">
            <label className="block text-xs font-semibold text-[#68665E]">Search or add a division under {company.name}</label>
            <button type="button" onClick={() => setStep(1)} className="btn-ghost text-xs" data-testid="wizard-back-1"><ArrowLeft className="w-3.5 h-3.5" /> Company</button>
          </div>
          <div className="relative">
            <Search className="w-4 h-4 text-[#9A968C] absolute left-3 top-1/2 -translate-y-1/2" />
            <input className="input-field w-full pl-9" placeholder="e.g. Construction, Hydrocarbon, Head Office…"
              value={dQuery} onChange={(e) => setDQuery(e.target.value)} data-testid="wizard-division-search" autoFocus />
          </div>
          <div className="mt-2 space-y-1">
            {divisionMatches.map((d) => (
              <button type="button" key={d.id} onClick={() => pickDivision(d)} data-testid={`wizard-division-pick-${d.id}`}
                className="flex items-center justify-between w-full text-left rounded border border-[#EAE7E0] hover:border-[#C84B31] hover:bg-[#FBF3F1] px-3 py-2 transition-colors">
                <span className="font-medium text-[#1A1A18] text-sm">{d.name}{d.gst_number ? <span className="ml-2 font-mono text-[11px] text-[#9A968C]">{d.gst_number}</span> : null}</span>
                <span className="text-[11px] text-[#9A968C]">Select <ChevronRight className="w-3 h-3 inline" /></span>
              </button>
            ))}
            {dQuery.trim() && !exactDivision && (
              <button type="button" onClick={createDivision} disabled={busy} data-testid="wizard-division-create"
                className="flex items-center gap-2 w-full text-left rounded border border-dashed border-[#C84B31] text-[#C84B31] hover:bg-[#FBF3F1] px-3 py-2 text-sm font-semibold transition-colors disabled:opacity-50">
                <Plus className="w-4 h-4" /> Create new division “{dQuery.trim()}”
              </button>
            )}
            {!dQuery.trim() && divisionMatches.length === 0 && (
              <p className="text-xs text-[#9A968C] py-2">No divisions yet for this company — type a name to add one.</p>
            )}
          </div>
        </div>
      )}

      {/* ---------- Step 3: GST ---------- */}
      {step === 3 && division && (
        <div data-testid="wizard-gst-step">
          <div className="flex items-center justify-between mb-1.5">
            <label className="block text-xs font-semibold text-[#68665E]">GST number for {division.name}</label>
            <button type="button" onClick={() => setStep(2)} className="btn-ghost text-xs" data-testid="wizard-back-2"><ArrowLeft className="w-3.5 h-3.5" /> Division</button>
          </div>
          <GstVerifyField className="input-field font-mono w-full" placeholder="e.g. 27AAACL0140P1Z8"
            value={gst} onChange={setGst} address="" testId="wizard-gst" />
          {gstDupe && <p className="text-[11px] text-[#B5651D] mt-1.5" data-testid="wizard-gst-dupe">⚠ This GST is already used by another division of this company — double-check before saving.</p>}
          <div className="flex items-center gap-2 mt-4">
            <button type="button" onClick={saveGst} disabled={busy} className="btn-primary" data-testid="wizard-gst-save">Save &amp; continue <ChevronRight className="w-4 h-4" /></button>
            <button type="button" onClick={() => setStep(4)} className="btn-ghost text-sm" data-testid="wizard-gst-skip">Skip for now</button>
          </div>
        </div>
      )}

      {/* ---------- Step 4: Addresses ---------- */}
      {step === 4 && division && (
        <div data-testid="wizard-address-step">
          <div className="flex items-center justify-between mb-2">
            <label className="block text-xs font-semibold text-[#68665E]">Addresses for {division.name} <span className="text-[#9A968C]">(add as many as you need)</span></label>
            <button type="button" onClick={() => setStep(3)} className="btn-ghost text-xs" data-testid="wizard-back-3"><ArrowLeft className="w-3.5 h-3.5" /> GST</button>
          </div>

          {divAddresses.length > 0 && (
            <div className="space-y-1 mb-3" data-testid="wizard-address-list">
              {divAddresses.map((a) => (
                <div key={a.id} className="flex items-start justify-between gap-2 rounded border border-[#EAE7E0] bg-[#FAF9F6] px-3 py-2" data-testid={`wizard-address-item-${a.id}`}>
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-[#1A1A18]">{a.label || "Address"} <span className="text-[10px] uppercase tracking-wide text-[#9A968C] ml-1">{a.kind}</span></div>
                    <div className="text-xs text-[#68665E] truncate">{a.address}</div>
                  </div>
                  <button type="button" onClick={() => delAddress(a.id)} className="btn-ghost text-[#8A2526] shrink-0" data-testid={`wizard-address-del-${a.id}`}><Trash2 className="w-4 h-4" /></button>
                </div>
              ))}
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-4 gap-2 items-end">
            <input className="input-field md:col-span-1" placeholder="Label (e.g. Plant, Store)" value={addrForm.label} onChange={(e) => setAddrForm({ ...addrForm, label: e.target.value })} data-testid="wizard-address-label" />
            <AddressAutocomplete className="input-field w-full" containerClassName="md:col-span-2" placeholder="Full address" value={addrForm.address} onChange={(v) => setAddrForm({ ...addrForm, address: v })} onSelect={(v) => setAddrForm({ ...addrForm, address: v })} testId="wizard-address-input" />
            <select className="input-field md:col-span-1" value={addrForm.kind} onChange={(e) => setAddrForm({ ...addrForm, kind: e.target.value })} data-testid="wizard-address-kind">
              <option value="both">Pickup &amp; delivery</option>
              <option value="pickup">Pickup only</option>
              <option value="delivery">Delivery only</option>
            </select>
            <button type="button" onClick={addAddress} disabled={busy || !addrForm.address.trim()} className="btn-secondary md:col-span-1" data-testid="wizard-address-add"><Plus className="w-4 h-4" /> Add address</button>
          </div>

          <div className="flex items-center gap-2 mt-4">
            <button type="button" onClick={() => setStep(5)} className="btn-primary" data-testid="wizard-address-continue">Continue to POCs <ChevronRight className="w-4 h-4" /></button>
          </div>
        </div>
      )}

      {/* ---------- Step 5: POCs ---------- */}
      {step === 5 && division && (
        <div data-testid="wizard-poc-step">
          <div className="flex items-center justify-between mb-2">
            <label className="block text-xs font-semibold text-[#68665E]">Points of contact for {division.name} <span className="text-[#9A968C]">(add as many as you need)</span></label>
            <button type="button" onClick={() => setStep(4)} className="btn-ghost text-xs" data-testid="wizard-back-4"><ArrowLeft className="w-3.5 h-3.5" /> Address</button>
          </div>

          {divPocs.length > 0 && (
            <div className="space-y-1 mb-3" data-testid="wizard-poc-list">
              {divPocs.map((p) => (
                <div key={p.id} className="flex items-center justify-between gap-2 rounded border border-[#EAE7E0] bg-[#FAF9F6] px-3 py-2" data-testid={`wizard-poc-item-${p.id}`}>
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-[#1A1A18]">{p.name}{p.designation ? <span className="text-xs text-[#9A968C] ml-2">{p.designation}</span> : null}</div>
                    <div className="text-xs text-[#68665E] truncate">{[p.phone, p.email].filter(Boolean).join(" · ") || "—"}</div>
                  </div>
                  <button type="button" onClick={() => delPoc(p.id)} className="btn-ghost text-[#8A2526] shrink-0" data-testid={`wizard-poc-del-${p.id}`}><Trash2 className="w-4 h-4" /></button>
                </div>
              ))}
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-4 gap-2 items-end">
            <input className="input-field" placeholder="Name" value={pocForm.name} onChange={(e) => setPocForm({ ...pocForm, name: e.target.value })} data-testid="wizard-poc-name" />
            <input className="input-field" placeholder="Phone" value={pocForm.phone} onChange={(e) => setPocForm({ ...pocForm, phone: e.target.value })} data-testid="wizard-poc-phone" />
            <input className="input-field" placeholder="Designation" value={pocForm.designation} onChange={(e) => setPocForm({ ...pocForm, designation: e.target.value })} data-testid="wizard-poc-designation" />
            <input type="email" className="input-field" placeholder="Email" value={pocForm.email} onChange={(e) => setPocForm({ ...pocForm, email: e.target.value })} data-testid="wizard-poc-email" />
            {pocDupe && <p className="md:col-span-4 text-[11px] text-[#B5651D]" data-testid="wizard-poc-dupe">⚠ A POC with this name already exists in this division.</p>}
            <button type="button" onClick={addPoc} disabled={busy || !pocForm.name.trim()} className="btn-secondary md:col-span-1" data-testid="wizard-poc-add"><Plus className="w-4 h-4" /> Add POC</button>
          </div>

          <div className="flex flex-wrap items-center gap-2 mt-5 pt-4 border-t border-[#EAE7E0]">
            <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#2F6B34]" data-testid="wizard-done"><Check className="w-4 h-4" /> Saved to directory</span>
            <div className="flex-1" />
            <button type="button" onClick={addAnotherDivision} className="btn-secondary" data-testid="wizard-add-division"><Layers className="w-4 h-4" /> Add another division</button>
            <button type="button" onClick={resetAll} className="btn-ghost text-sm" data-testid="wizard-restart"><RotateCcw className="w-4 h-4" /> New company</button>
          </div>
        </div>
      )}
    </section>
  );
}
